import React, { useEffect, useState, useCallback } from 'react';
import { Search, Plus } from 'lucide-react';
import { productsApi } from '../api/products';
import { useToast } from '../hooks/useToast';

interface Product {
  id: number; sku: string; name: string; product_type: string;
  cost_price: number; selling_price: number; category_name: string;
  unit_symbol: string; min_reorder_level: number; is_active: boolean;
}
interface Category { id: number; name: string; costing_method: string; }
interface Unit     { id: number; name: string; symbol: string; }

const typeBadge: Record<string, string> = {
  inventory: 'badge-blue', non_inventory: 'badge-amber', service: 'badge-purple'
};

const Products: React.FC = () => {
  const { showToast } = useToast();
  const [products, setProducts]     = useState<Product[]>([]);
  const [total, setTotal]           = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [units, setUnits]           = useState<Unit[]>([]);
  const [search, setSearch]         = useState('');
  const [loading, setLoading]       = useState(true);
  const [showForm, setShowForm]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [showCatForm, setShowCatForm] = useState(false);
  const [showUnitForm, setShowUnitForm] = useState(false);
  const [newCat, setNewCat]   = useState({ name: '', costing_method: 'AVCO' });
  const [newUnit, setNewUnit] = useState({ name: '', symbol: '' });
  const [form, setForm] = useState({
    sku: '', name: '', description: '', category_id: '',
    unit_id: '', product_type: 'inventory',
    cost_price: '0', selling_price: '0',
    min_reorder_level: '0', reorder_qty: '0',
    has_batches: false, has_expiry: false,
  });

  const loadMeta = () => {
    productsApi.categories().then(r => setCategories(r.data.data));
    productsApi.units().then(r => setUnits(r.data.data));
  };

  const load = useCallback(() => {
    setLoading(true);
    productsApi.list({ search }).then(res => {
      setProducts(res.data.data);
      setTotal(res.data.total);
      setLoading(false);
    });
  }, [search]);

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const handleCreate = async () => {
    if (!form.sku || !form.name || !form.category_id || !form.unit_id) {
      showToast('SKU, Name, Category and Unit are required', 'error'); return;
    }
    setSaving(true);
    try {
      await productsApi.create({ ...form, category_id: Number(form.category_id), unit_id: Number(form.unit_id), cost_price: Number(form.cost_price), selling_price: Number(form.selling_price), min_reorder_level: Number(form.min_reorder_level), reorder_qty: Number(form.reorder_qty) });
      showToast('Product created!');
      setShowForm(false);
      setForm({ sku:'', name:'', description:'', category_id:'', unit_id:'', product_type:'inventory', cost_price:'0', selling_price:'0', min_reorder_level:'0', reorder_qty:'0', has_batches:false, has_expiry:false });
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleCreateCat = async () => {
    if (!newCat.name) { showToast('Name required', 'error'); return; }
    try { await productsApi.createCategory(newCat); showToast('Category created!'); setNewCat({ name:'', costing_method:'AVCO' }); setShowCatForm(false); loadMeta(); }
    catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  const handleCreateUnit = async () => {
    if (!newUnit.name || !newUnit.symbol) { showToast('Name and symbol required', 'error'); return; }
    try { await productsApi.createUnit(newUnit); showToast('Unit created!'); setNewUnit({ name:'', symbol:'' }); setShowUnitForm(false); loadMeta(); }
    catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Products</h1>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowCatForm(!showCatForm)}>+ Category</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowUnitForm(!showUnitForm)}>+ Unit</button>
          <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}><Plus size={13} /> Add Product</button>
        </div>
      </div>

      {showCatForm && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>New Category</span><button className="btn btn-secondary btn-sm" onClick={() => setShowCatForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3">
              <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={newCat.name} onChange={e => setNewCat(c=>({...c,name:e.target.value}))} placeholder="e.g. Electronics" /></div>
              <div className="form-group"><label className="form-label">Costing Method</label>
                <select className="form-control" value={newCat.costing_method} onChange={e => setNewCat(c=>({...c,costing_method:e.target.value}))}>
                  <option value="AVCO">AVCO</option><option value="FIFO">FIFO</option><option value="LIFO">LIFO</option>
                </select>
              </div>
            </div>
            <button className="btn btn-primary" onClick={handleCreateCat}>Save Category</button>
          </div>
        </div>
      )}

      {showUnitForm && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>New Unit</span><button className="btn btn-secondary btn-sm" onClick={() => setShowUnitForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3">
              <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={newUnit.name} onChange={e => setNewUnit(u=>({...u,name:e.target.value}))} placeholder="e.g. Kilogram" /></div>
              <div className="form-group"><label className="form-label">Symbol *</label><input className="form-control" value={newUnit.symbol} onChange={e => setNewUnit(u=>({...u,symbol:e.target.value}))} placeholder="e.g. kg" /></div>
            </div>
            <button className="btn btn-primary" onClick={handleCreateUnit}>Save Unit</button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header"><span>New Product</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3">
              <div className="form-group"><label className="form-label">SKU *</label><input className="form-control mono" value={form.sku} onChange={e => setForm(f=>({...f,sku:e.target.value}))} placeholder="PROD-001" /></div>
              <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="Product name" /></div>
              <div className="form-group"><label className="form-label">Type</label>
                <select className="form-control" value={form.product_type} onChange={e => setForm(f=>({...f,product_type:e.target.value}))}>
                  <option value="inventory">Inventory</option>
                  <option value="non_inventory">Non-Inventory</option>
                  <option value="service">Service</option>
                </select>
              </div>
            </div>
            <div className="form-grid-3">
              <div className="form-group"><label className="form-label">Category *</label>
                <select className="form-control" value={form.category_id} onChange={e => setForm(f=>({...f,category_id:e.target.value}))}>
                  <option value="">Select category</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Unit *</label>
                <select className="form-control" value={form.unit_id} onChange={e => setForm(f=>({...f,unit_id:e.target.value}))}>
                  <option value="">Select unit</option>
                  {units.map(u => <option key={u.id} value={u.id}>{u.name} ({u.symbol})</option>)}
                </select>
              </div>
              <div className="form-group"><label className="form-label">Description</label><input className="form-control" value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} /></div>
            </div>
            <div className="form-grid-4">
              <div className="form-group"><label className="form-label">Cost Price</label><input className="form-control mono" type="number" value={form.cost_price} onChange={e => setForm(f=>({...f,cost_price:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Selling Price</label><input className="form-control mono" type="number" value={form.selling_price} onChange={e => setForm(f=>({...f,selling_price:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Reorder Level</label><input className="form-control mono" type="number" value={form.min_reorder_level} onChange={e => setForm(f=>({...f,min_reorder_level:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Reorder Qty</label><input className="form-control mono" type="number" value={form.reorder_qty} onChange={e => setForm(f=>({...f,reorder_qty:e.target.value}))} /></div>
            </div>
            <div style={{ display:'flex', gap:16, marginBottom:14 }}>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer' }}>
                <input type="checkbox" checked={form.has_batches} onChange={e => setForm(f=>({...f,has_batches:e.target.checked}))} /> Track Batches
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, cursor:'pointer' }}>
                <input type="checkbox" checked={form.has_expiry} onChange={e => setForm(f=>({...f,has_expiry:e.target.checked}))} /> Track Expiry
              </label>
            </div>
            <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Saving...' : 'Save Product'}</button>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-box"><Search size={13} color="var(--tx3)" /><input placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        <span className="record-count">{total} products</span>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>SKU</th><th>Name</th><th>Category</th><th>Type</th><th>Unit</th><th style={{textAlign:'right'}}>Cost</th><th style={{textAlign:'right'}}>Price</th><th>Status</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{textAlign:'center',padding:32,color:'var(--tx3)'}}>Loading...</td></tr>
            ) : products.length === 0 ? (
              <tr><td colSpan={8}><div className="empty-state"><p>No products yet.</p></div></td></tr>
            ) : products.map(p => (
              <tr key={p.id}>
                <td className="td-code">{p.sku}</td>
                <td style={{fontWeight:600}}>{p.name}</td>
                <td style={{color:'var(--tx3)'}}>{p.category_name}</td>
                <td><span className={`badge ${typeBadge[p.product_type]||'badge-gray'}`}>{p.product_type}</span></td>
                <td style={{color:'var(--tx3)'}}>{p.unit_symbol}</td>
                <td className="td-num">{Number(p.cost_price).toFixed(2)}</td>
                <td className="td-num td-green">{Number(p.selling_price).toFixed(2)}</td>
                <td><span className={`badge ${p.is_active ? 'badge-green' : 'badge-gray'}`}>{p.is_active ? 'Active' : 'Inactive'}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default Products;
